/**
 * The photograph `FLY A HISTORIC MISSION` puts in its upper right corner — the
 * one piece of a historic mission's own presentation the port was missing.
 *
 * The manual says it outright (`docs/game/manual/04-gameplay.md`, *Fly A
 * Historic Mission*): *"The upper right corner of the screen displays a
 * picture of the famous ace featured in the mission."* The shipped capture
 * `docs/game/ui-reference/historic-mission-listbox.png` shows it: a 64×64
 * black-and-white period photograph of Hawker, sunk into the tan body beside
 * the encounter list.
 *
 * **Where it comes from.** `HEADS.BMP` — 27 frames, every one 64×64, the only
 * portrait sheet in the game and the one file the Mission Builder expansion
 * added new art to (21 frames → 27, in lockstep with `ACE.DAT` going 19 → 25
 * records). Template-matching the capture's portrait region against the sheet
 * lands on **frame 15** at **(224, 33)**, and Hawker is `ACE.DAT` record 15;
 * doing the same to `dogfight-famous-ace-roster.png`, which has Richthofen
 * selected, lands on **frame 0**, and Richthofen is record 0. So the frame is
 * the seat's `ACE.DAT` record index, with no remapping — `HISTORIC.DAT`'s own
 * `aceId` byte, straight through.
 *
 * **The two frames past the roster.** 27 frames for 25 aces, and the extra two
 * are not portraits at all: frame 25 is a photograph of a formation of
 * aeroplanes, frame 26 one of two observation balloons on their cables. They
 * are the fallbacks for `HISTORIC.DAT`'s `100`/`101` unnamed-pilot codes,
 * which is why the file bothers to write two different values there — see
 * `UNNAMED_PILOT_FRAME` for which is which, and how far that is proven.
 *
 * **It follows the seat, not the encounter.** The file's clinching detail is
 * Lt. Roth Goes Balloon Hunting, whose two seats carry *different* unnamed
 * codes (`101` German, `100` Allied). A per-encounter picture would have no
 * use for two, so the byte the picture is chosen from is read per seat — the
 * same way the prose is, which the manual states explicitly (*"The text
 * description of the mission will change to reflect your mission, depending
 * upon which side you choose to fly on."*). The capture agrees: it shows the
 * Allied seat's lines and the Allied seat's ace.
 */

import { historicEncounter, type Nation, seatOf } from "@red-opus/engine";
import type { Rect } from "../render/draw-ops.ts";
import type { Widget } from "./shell-screen.ts";

export const PORTRAIT_SHEET = "sprites/HEADS.BMP";

/** Measured off `historic-mission-listbox.png`: the photograph's own 64×64 square. */
export const PORTRAIT_ORIGIN = { x: 224, y: 33 } as const;
export const PORTRAIT_SIZE = 64;

/**
 * The well the photograph is sunk into: the standard two-ring sunken frame,
 * two pixels proud of the picture on every side. The capture's row 33 reads
 * `(154,105,69)` at x=222 and `(130,89,56)` at x=223 before the first grey of
 * the photograph at x=224 — which is `PS.PAL` 232 then 234, i.e. exactly the
 * side colours of `SUNKEN_RINGS`, in order.
 */
export const PORTRAIT_WELL: Rect = {
  x: PORTRAIT_ORIGIN.x - 2,
  y: PORTRAIT_ORIGIN.y - 2,
  w: PORTRAIT_SIZE + 4,
  h: PORTRAIT_SIZE + 4,
};

/**
 * Which of the two non-portrait frames each unnamed-pilot code selects.
 * **INFERRED**, and the argument is exhaustion over the six seats that carry
 * one, because there are only two ways round and one of them is absurd:
 *
 * - `100` appears on exactly two seats, and both are the man **guarding the
 *   balloons** — the Sopwith Camel commander Roth is hunting past, and the
 *   Fokker D.VII commander defending the Meuse gasbags against Frank Luke.
 *   Frame 26, the balloons, is a picture of what he is there to protect.
 * - `101` appears on four, and gives all four the formation photograph: Roth
 *   himself, Roy Brown, the S.E.5a commander going for Udet's Drachen, and the
 *   Sopwith Triplane commander of Naval 10.
 *
 * Taken the other way round, frame 26 lands on Roy Brown and on Naval 10 —
 * balloons on two seats of two pure dogfights, neither of which has a balloon
 * anywhere in it. So this way round, and the residual doubt is honest: a
 * DOSBox run of encounter 2 or 7 would settle it by eye in a minute, and the
 * static route cannot.
 */
export const UNNAMED_PILOT_FRAME: Readonly<Record<number, number>> = {
  100: 26,
  101: 25,
};

/**
 * The frame this seat's photograph is, or nothing when the encounter or the
 * seat is not one the sheet can answer for (an unplayable seat has no `aceId`
 * at all — the file writes `FF FF FF`).
 */
export function historicPortraitFrame(catalogId: string, nation: Nation): number | undefined {
  const data = historicEncounter(catalogId);
  if (!data) return undefined;
  const seat = seatOf(data, nation);
  if (!seat.playable) return undefined;
  if (seat.aceIndex !== undefined) return seat.aceIndex;
  return seat.unnamedPilot === undefined ? undefined : UNNAMED_PILOT_FRAME[seat.unnamedPilot];
}

/**
 * The well and the photograph, or an empty list for every mission type that is
 * not a historic encounter — which is what lets the briefing screen call this
 * unconditionally instead of branching on the catalog id itself.
 */
export function historicPortraitWidgets(catalogId: string, nation: Nation): Widget[] {
  const frame = historicPortraitFrame(catalogId, nation);
  if (frame === undefined) return [];
  return [
    { kind: "well", rect: PORTRAIT_WELL },
    { kind: "picture", sheet: PORTRAIT_SHEET, frame, x: PORTRAIT_ORIGIN.x, y: PORTRAIT_ORIGIN.y },
  ];
}
